import { unwrapResult } from '@reduxjs/toolkit';
import { useAppSelector } from 'hooks';
import { normalize } from 'normalizr';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { AppDispatch } from 'redux/store';
import {
  LayoutEditResponse, LayoutEntry, LayoutResponse, RoleModel,
} from 'types/entity';
import { LayoutEditSchema } from 'types/schema/layout-schema';
import { layoutSchema } from './layout-update.adapter';
import { layoutUpdateQuery } from './layout-update.selector';
import { layoutUpdateActions } from './layout-update.slice';

export const useLayoutUpdateFacade = () => {
  const dispatch: AppDispatch = useDispatch();

  /** ロード済みか */
  const loaded = useAppSelector(layoutUpdateQuery.getLoaded);
  /** 更新中か */
  const processing = useAppSelector(layoutUpdateQuery.getProcessing);
  /** 編集中か */
  const touched = useAppSelector(layoutUpdateQuery.getTouched);
  /** レイアウト一覧 */
  const layouts: LayoutEditResponse[] = useAppSelector(layoutUpdateQuery.getEntities);
  /** レイアウト一覧（LOCALE指定） */
  const layoutsByLocale: LayoutResponse[] = useAppSelector(layoutUpdateQuery.getEntitiesByLocale);
  /** 選択中のレイアウト */
  const selectedLayout: LayoutEditResponse | undefined = useAppSelector(layoutUpdateQuery.getSelectedEntity);
  /** 選択中のレイアウト（LOCALE指定） */
  const selectedLayoutByLocale: LayoutResponse | undefined = useAppSelector(
    layoutUpdateQuery.getSelectedEntityByLocale,
  );

  /** ロード */
  const loadLayout = useCallback(
    (projectId: string) => dispatch(layoutUpdateActions.loadLayouts(projectId)).then(unwrapResult),
    [dispatch],
  );
  /** レイアウト選択 */
  const selectLayout = useCallback(
    (layoutId: string) => dispatch(layoutUpdateActions.selectOne(layoutId)),
    [dispatch],
  );
  /** 保存 */
  const saveLayout = useCallback(
    (payload: {projectId: string, layoutId: string, layout: LayoutEntry}) => dispatch(layoutUpdateActions.saveLayout(payload)).then(unwrapResult),
    [dispatch],
  );
  /** ガジェット削除、追加 */
  const updateGadgetRef = useCallback(
    (layout: LayoutEditResponse) => {
      const normalized = normalize(layout, layoutSchema);
      return dispatch(layoutUpdateActions.updateGadgetRef(normalized.entities as LayoutEditSchema));
    },
    [dispatch],
  );
  /** 役割の変更 */
  const updateRoles = useCallback(
    (layoutId: string, roles: RoleModel[]) => dispatch(layoutUpdateActions.updateOne({ layoutId, roles })),
    [dispatch],
  );
  /** 選択中状態の初期化 */
  const clear = useCallback(
    () => dispatch(layoutUpdateActions.clear()),
    [dispatch],
  );
  /** 編集中状態の初期化 */
  const unTouched = useCallback(
    () => dispatch(layoutUpdateActions.unTouched()),
    [dispatch],
  );

  return {
    loaded,
    processing,
    touched,
    layouts,
    layoutsByLocale,
    selectedLayout,
    selectedLayoutByLocale,
    loadLayout,
    selectLayout,
    saveLayout,
    updateGadgetRef,
    updateRoles,
    clear,
    unTouched,
  };
};
